import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getFestivalEvent, createFestivalCheckout, getFestivalOrderReceipt } from "../lib/api";
import { bedLayoutsForSleeps } from "../lib/bed-layout";
import { buildWorkshopSlots, buildCheckoutWorkshops, workshopChoiceNeeded } from "../lib/workshop-picks";
import { WorkshopTimetable, WorkshopPicker } from "../components/workshop-picker";
import { computeBuyerPlatformFee } from "../lib/fees";
import type { FestivalEvent, FestivalTier, FestivalAddOn } from "../lib/types";
import { useAuth } from "../lib/auth";
import { useVenue } from "../lib/venue";
import { pushToast } from "../components/toast";
import { resolveFestivalContent } from "../lib/festival-content";
import { pushEcommerceEvent, pushEcommerceEventThenNavigate, usePurchaseEvent } from "../lib/analytics";
import {
  FestivalHeroSection,
  FestivalStoryTop,
  FestivalStoryBottom,
  FestivalBackdrop,
  FestivalFontFace,
  FestivalSplash,
} from "./festival-story";

// Kaboodle-style single-scroll festival buyer journey. Sections stack down the
// page (hero → tickets → extras → accommodation → details) with a live,
// sticky order summary the buyer can always see. Multi-item order: any mix of
// ticket tiers + add-ons + accommodation goes into one Stripe Checkout.

// Relative so it stays on the venue's own domain (e.g. madrugafestival.co.uk/
// privacy), the LegalPage renders on any host (see main.tsx). Absolute
// www.assemblytickets.com doesn't resolve; the apex + every venue host do.
const TERMS_URL = "/privacy";

const gbp = (cents: number) => `£${(cents / 100).toFixed(2)}`;

// A 3-installment payment plan is the one option we surface (keeps it simple).
const PAYMENT_PLAN_INSTALLMENTS = 3;
// Payment plans are opt-in per ticket tier (admin toggle, off by default, e.g.
// the presale sells full-payment only). The option is offered only when every
// selected ticket tier allows it; the backend enforces the same rule. When on,
// it caps the Stripe subscription at N charges and issues tickets up front on
// the deposit invoice, see order-checkout.ts / order-webhook.ts.

// ── Tier sales-window state ─────────────────────────────────────────────────
// The server enforces salesStart/salesEnd at checkout (a 409). The buyer UI
// must mirror that so a tier that isn't currently on sale shows its real
// status instead of a buy button that 409s. Precedence: sold out → not yet
// open → closed → open.
type TierSaleState =
  | { kind: "open"; cap: number | null }
  | { kind: "soldout" }
  | { kind: "upcoming"; startsAt: Date }
  | { kind: "ended" };

function tierSaleState(tier: FestivalTier, now: Date): TierSaleState {
  if (tier.remaining != null && tier.remaining <= 0) return { kind: "soldout" };
  if (tier.salesStart) {
    const start = new Date(tier.salesStart);
    if (!Number.isNaN(start.getTime()) && now < start) return { kind: "upcoming", startsAt: start };
  }
  if (tier.salesEnd) {
    const end = new Date(tier.salesEnd);
    if (!Number.isNaN(end.getTime()) && now > end) return { kind: "ended" };
  }
  return { kind: "open", cap: capOf(tier.remaining, tier.maxPerOrder) };
}

const onSaleLabel = (d: Date) =>
  `On sale ${d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · ${d
    .toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" })
    .toLowerCase()}`;

// "2d 4h 9m" / "4h 9m 12s" / "9m 12s", a live countdown to the on-sale moment.
function formatCountdown(ms: number): string {
  if (ms <= 0) return "moments";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  return `${m}m ${sec}s`;
}

type Quantities = Record<string, number>; // itemId → qty (tiers + add-ons keyed together)

// Which slice of the festival to render. "full" = the whole scroll (legacy /
// direct link); the others are the separate minimal pages of the website.
export type FestivalVariant = "full" | "splash" | "lineup" | "whatsOn" | "info" | "tickets";

export function FestivalPage({
  eventId,
  variant = "full",
}: { eventId?: string; variant?: FestivalVariant } = {}) {
  // eventId prop wins (venue-root landing mode); otherwise read the /festival/$id param.
  const params = useParams({ strict: false }) as { id?: string };
  const id = eventId ?? params.id ?? "";
  // Guest festival purchases land here (/tickets?purchased=<orderId>&kind=festival
  // on a FESTIVAL venue, see router.tsx's TicketsRoute). Signed-in purchases
  // land on /my-tickets instead, always TicketsPage, handled in tickets.tsx.
  const purchasedId =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("purchased") : null;
  const justPurchased = !!purchasedId;
  usePurchaseEvent(purchasedId, getFestivalOrderReceipt);

  // Tokenised presale access code from the private link (?k=…). The server gates
  // the tiers on it; without a valid code the event comes back `locked`.
  const accessCode =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("k") ?? undefined
      : undefined;

  const query = useQuery({
    queryKey: ["festival", id, accessCode ?? ""],
    queryFn: () => getFestivalEvent(id, accessCode),
    retry: false,
  });

  const [qty, setQty] = useState<Quantities>({});

  if (query.isLoading) {
    return <div className="p-8 text-sm text-[var(--foreground)]/70">Loading…</div>;
  }
  if (query.error || !query.data) {
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="text-3xl font-bold text-[var(--foreground)]">Festival not found</h1>
        <p className="mt-2 text-sm text-[var(--muted)]">
          This event isn’t available right now.
        </p>
      </div>
    );
  }

  const data = query.data;
  // Invite-only presale: the ?k= code was missing/wrong, so there's no buyable
  // inventory. Only gate the *buy* page, the homepage sign-up splash and the
  // marketing pages (lineup/what's-on/info) must still render for the public,
  // otherwise a private presale takes the whole public site down with it.
  if (data.locked && variant === "tickets") {
    return (
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="text-3xl font-bold text-[var(--foreground)]">{data.event.title}</h1>
        <p className="mt-3 text-sm text-[var(--muted)]">
          This presale is invite-only. Please use the private link you were sent, or sign up
          on the homepage to hear when tickets go public.
        </p>
      </div>
    );
  }
  const currency = data.event.currency || "GBP";

  return (
    <FestivalContent
      eventId={id}
      data={data}
      currency={currency}
      qty={qty}
      setQty={setQty}
      justPurchased={justPurchased}
      variant={variant}
    />
  );
}

function FestivalContent({
  eventId,
  data,
  currency,
  qty,
  setQty,
  justPurchased,
  variant,
}: {
  eventId: string;
  data: FestivalEvent;
  currency: string;
  qty: Quantities;
  setQty: React.Dispatch<React.SetStateAction<Quantities>>;
  justPurchased: boolean;
  variant: FestivalVariant;
}) {
  const { event, tiers, sessions, addOns, accommodation } = data;
  const { data: venueData } = useVenue();
  const story = resolveFestivalContent(venueData?.venue);

  // Workshop picks per tier (tierId → chosen workshop session ids). Only used
  // by events with a WORKSHOP schedule; the server re-validates at checkout.
  const [workshopPicks, setWorkshopPicks] = useState<Record<string, string[]>>({});
  const setTierPick = (tierId: string, next: string[]) =>
    setWorkshopPicks((prev) => ({ ...prev, [tierId]: next }));
  const workshopSlots = useMemo(() => buildWorkshopSlots(sessions), [sessions]);
  const { workshops: workshopSelections, complete: workshopsComplete } = useMemo(
    () => buildCheckoutWorkshops(tiers, workshopSlots, qty, workshopPicks),
    [tiers, workshopSlots, qty, workshopPicks],
  );

  // Guest confirmation: fetch the order receipt so we can link straight to the
  // ticket(s) on the success screen, a guest's only durable route back, so we
  // don't leave them dependent on the confirmation email arriving.
  const purchasedId =
    typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("purchased") : null;
  const receiptQuery = useQuery({
    queryKey: ["festival-receipt", purchasedId],
    queryFn: () => getFestivalOrderReceipt(purchasedId!),
    enabled: justPurchased && !!purchasedId,
    // Poll through the webhook's flip-to-paid lag until the tickets exist.
    refetchInterval: (q) => (q.state.data?.ready ? false : 2500),
    retry: false,
  });
  const purchasedTicketIds = receiptQuery.data?.ready ? receiptQuery.data.ticketIds ?? [] : [];
  // "Buy" splash: the landing shows the logo + a Buy-tickets CTA, then fades
  // into the purchase flow when tapped.
  const [buyStarted, setBuyStarted] = useState(false);
  // Chosen bed layout per accommodation unit (addOnId → e.g. "2 doubles").
  const [bedLayouts, setBedLayouts] = useState<Record<string, string>>({});
  const setBedLayout = (id: string, label: string) =>
    setBedLayouts((prev) => ({ ...prev, [id]: label }));

  const setQuantity = (itemId: string, next: number) =>
    setQty((prev) => {
      const current = prev[itemId] ?? 0;
      // Only fire on an actual increase, a decrease/removal isn't GA4's
      // add_to_cart (there's no standard "remove" counterpart wired up here).
      if (next > current) {
        const tier = tiers.find((t) => t.id === itemId);
        const addOn = tier ? undefined : [...addOns, ...accommodation].find((a) => a.id === itemId);
        const item = tier ?? addOn;
        if (item) {
          pushEcommerceEvent("add_to_cart", {
            currency,
            value: (item.priceCents / 100) * (next - current),
            items: [
              {
                item_id: item.id,
                item_name: item.name,
                item_category: tier ? "ticket" : accommodation.some((a) => a.id === itemId) ? "accommodation" : "add-on",
                price: item.priceCents / 100,
                quantity: next - current,
              },
            ],
          });
        }
      }
      const copy = { ...prev };
      if (next <= 0) delete copy[itemId];
      else copy[itemId] = next;
      return copy;
    });

  // Live order summary lines. Tiers first, then extras, then accommodation.
  const lines = useMemo(() => {
    const out: Array<{ id: string; name: string; qty: number; priceCents: number }> = [];
    for (const t of tiers) {
      const q = qty[t.id] ?? 0;
      // Only count tiers currently on sale, mirrors the server's checkout
      // guard so a stale quantity (tier closed/sold out since selection) can't
      // reach checkout and 409.
      if (q > 0 && tierSaleState(t, new Date()).kind === "open") {
        out.push({ id: t.id, name: t.name, qty: q, priceCents: t.priceCents });
      }
    }
    for (const a of [...addOns, ...accommodation]) {
      if (a.variants && a.variants.length > 0) {
        // Sized add-on: one line per chosen size, all at the add-on's price.
        for (const v of a.variants) {
          const q = qty[v.id] ?? 0;
          if (q > 0) out.push({ id: v.id, name: `${a.name} (${v.label})`, qty: q, priceCents: a.priceCents });
        }
      } else {
        const q = qty[a.id] ?? 0;
        if (q > 0) out.push({ id: a.id, name: a.name, qty: q, priceCents: a.priceCents });
      }
    }
    return out;
  }, [tiers, addOns, accommodation, qty]);

  const subtotalCents = lines.reduce((sum, l) => sum + l.qty * l.priceCents, 0);
  const hasTickets = lines.some((l) => tiers.some((t) => t.id === l.id));
  // Ticket-only subtotal, the platform fee applies to tickets, not add-ons
  // (e.g. a "£25 including shipping" tee carries no platform fee).
  const ticketsSubtotalCents = lines
    .filter((l) => tiers.some((t) => t.id === l.id))
    .reduce((sum, l) => sum + l.qty * l.priceCents, 0);
  // The organiser booking fee is a single per-order charge, it only applies
  // once there's a ticket in the order (matches the server, which requires ≥1
  // tier to create an order).
  // A second flat booking fee applies once when the cart has extras/accommodation
  // (folded into the one "Booking fee" line). Matches the server exactly.
  const addOnBookingFeeCents = hasTickets && subtotalCents - ticketsSubtotalCents > 0 ? event.addOnBookingFeeCents : 0;
  const bookingFeeCents = (hasTickets ? event.bookingFeeCents : 0) + addOnBookingFeeCents;
  // Buyer-facing platform fee (our margin + Stripe, shown as one line). Computed
  // identically to the server so the summary matches the Stripe charge exactly.
  const platformFeeCents = computeBuyerPlatformFee(ticketsSubtotalCents, event.platformFeeBps);
  const totalCents = subtotalCents + platformFeeCents + bookingFeeCents;

  // Payment-plan state lives here (not in CheckoutBlock) so the basket summary
  // can react to it too. All-in plan: every selected ticket tier has a fixed
  // plan price → the basket total swaps to that fixed sum (e.g. £240).
  const [payInInstallments, setPayInInstallments] = useState(false);
  const ticketLineIds = new Set(tiers.map((t) => t.id));
  const ticketLines = lines.filter((l) => ticketLineIds.has(l.id));
  const planAllInAvailable =
    ticketLines.length > 0 &&
    ticketLines.every((l) => {
      const t = tiers.find((x) => x.id === l.id);
      return !!t?.paymentPlanEnabled && t?.paymentPlanPriceCents != null;
    });
  const addOnsSubtotalCents = subtotalCents - ticketsSubtotalCents;
  // Basket lines re-priced at each tier's all-in plan price, for the plan view,
  // so the item reads at its plan price (e.g. £240) and the Total matches, with
  // the monthly split shown below.
  const planLines = lines.map((l) => {
    const t = tiers.find((x) => x.id === l.id);
    return t?.paymentPlanPriceCents != null ? { ...l, priceCents: t.paymentPlanPriceCents } : l;
  });
  const planTicketsCents = ticketLines.reduce((sum, l) => {
    const t = tiers.find((x) => x.id === l.id);
    return sum + l.qty * (t?.paymentPlanPriceCents ?? l.priceCents);
  }, 0);
  const planTotalCents = planAllInAvailable
    ? planTicketsCents + addOnsSubtotalCents + addOnBookingFeeCents
    : totalCents + Math.max(0, event.paymentPlanFeeCents);
  // The plan pricing only "takes over" the basket when it's toggled AND all-in.
  const planActive = payInInstallments && planAllInAvailable;
  // Where the plan total actually goes, for a transparent "3 × £80" breakdown:
  // Assembly's 2.5% application fee (feeBps as a % of the total, matches the
  // Stripe subscription the checkout creates), the organiser booking fee, and the
  // rest to the venue. Booking fee + venue share both settle to the organiser.
  const planPlatformFeeCents = Math.round((planTotalCents * event.platformFeeBps) / 10000);
  const planBookingFeeCents = bookingFeeCents; // combined base + extras booking fee
  const planVenueCents = Math.max(0, planTotalCents - planPlatformFeeCents - planBookingFeeCents);

  const dateLabel = new Date(event.date).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const endLabel = event.endDate
    ? new Date(event.endDate).toLocaleDateString("en-GB", { day: "numeric", month: "long" })
    : null;
  const location = event.venue ? `${event.venue}, ${event.city}` : event.city;

  const themeStyle = story?.theme
    ? ({
        "--accent": story.theme.accent,
        "--accent-fg": story.theme.accentFg,
        ...(story.theme.displayFont ? { fontFamily: story.theme.displayFont } : {}),
      } as React.CSSProperties)
    : undefined;

  // Post-purchase confirmation. Guests land on the buy page (/tickets) after
  // paying, they have no wallet, so this reassures them the ticket is emailed.
  const purchasedNotice = justPurchased ? (
    <div className="rounded-[var(--radius)] border border-[var(--accent)]/40 bg-[var(--accent)]/10 p-4 text-sm text-[var(--foreground)]">
      <p className="font-medium">You’re in! Your order is confirmed.</p>
      {purchasedTicketIds.length > 0 ? (
        <>
          <p className="mt-1 text-[var(--foreground)]/80">
            {purchasedTicketIds.length > 1
              ? "Here are your tickets. Bookmark this page, and we’ve emailed them to you too."
              : "Here’s your ticket. Bookmark this page, and we’ve emailed it to you too."}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {purchasedTicketIds.map((id, i) => (
              <a
                key={id}
                href={`/ticket/${id}`}
                className="inline-flex items-center rounded-full bg-[var(--accent)] px-4 py-1.5 text-sm font-medium"
                style={{ color: "var(--accent-fg)" }}
              >
                {purchasedTicketIds.length > 1 ? `View ticket ${i + 1}` : "View your ticket"}
              </a>
            ))}
          </div>
        </>
      ) : (
        <p className="mt-1 text-[var(--foreground)]/80">
          Your tickets and details are on their way to your email. This can take a few seconds.
        </p>
      )}
    </div>
  ) : null;

  const heroSection = (
    <FestivalHeroSection
      hero={story?.hero}
      fallbackImage={event.imageUrl}
      title={event.title}
      dateLine={`${dateLabel}${endLabel ? ` – ${endLabel}` : ""} · ${location}`}
      strapline={story?.strapline}
    />
  );

  const marketingSection = (
    <div className="mt-8 space-y-12">
      {event.description && (
        <p className="max-w-2xl whitespace-pre-line text-base leading-relaxed text-[var(--foreground)]/80">
          {event.description}
        </p>
      )}
      {event.cancelled && (
        <div className="rounded-[var(--radius)] border border-red-500/40 bg-red-500/10 p-4 text-sm font-medium text-red-200">
          This event has been cancelled. Tickets are no longer on sale. If you already bought, you’ll be contacted about a refund.
        </div>
      )}
      {purchasedNotice}
      {story && !event.cancelled && <FestivalStoryTop content={story} />}
    </div>
  );

  const ticketsSection = (
    <section id="tickets" className="scroll-mt-24">
      <div className="lg:grid lg:grid-cols-[1fr_340px] lg:gap-8">
        <div className="space-y-8">
          {sessions.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-[var(--foreground)]">Schedule</h2>
              <WorkshopTimetable sessions={sessions} />
            </section>
          )}

          <StepSection step={1} title="Tickets" subtitle="Pick your passes">
            {tiers.length === 0 ? (
              <EmptyNote>Tickets aren’t on sale yet. Check back soon.</EmptyNote>
            ) : (
              <div className="space-y-3">
                {tiers.map((t) => (
                  <TierCard key={t.id} tier={t} qty={qty[t.id] ?? 0} onChange={(n) => setQuantity(t.id, n)} />
                ))}
              </div>
            )}
          </StepSection>

          {addOns.length > 0 && (
            <StepSection step={2} title="Extras" subtitle="Add-ons for your trip">
              <div className="space-y-3">
                {addOns.map((a) => (
                  <AddOnCard key={a.id} addOn={a} qtyOf={(id) => qty[id] ?? 0} setQty={setQuantity} />
                ))}
              </div>
            </StepSection>
          )}

          {accommodation.length > 0 && (
            <StepSection step={3} title="Accommodation" subtitle="Somewhere to stay">
              <AccommodationSection accommodation={accommodation} qty={qty} setQuantity={setQuantity} bedLayouts={bedLayouts} setBedLayout={setBedLayout} />
            </StepSection>
          )}

          {workshopChoiceNeeded(tiers, workshopSlots, qty) && (
            <StepSection step={4} title="Workshops" subtitle="Choose your session">
              <WorkshopPicker
                tiers={tiers}
                sessions={sessions}
                qty={qty}
                picks={workshopPicks}
                onChangeTier={setTierPick}
              />
            </StepSection>
          )}

          <StepSection step={workshopChoiceNeeded(tiers, workshopSlots, qty) ? 5 : 4} title="Your details" subtitle="Almost there">
            <CheckoutBlock
              eventId={eventId}
              currency={currency}
              tiers={tiers}
              addOns={[...addOns, ...accommodation]}
              qty={qty}
              bedLayouts={bedLayouts}
              workshops={workshopSelections}
              workshopsComplete={workshopsComplete}
              totalCents={totalCents}
              hasTickets={hasTickets}
              paymentPlanFeeCents={event.paymentPlanFeeCents}
              payInInstallments={payInInstallments}
              setPayInInstallments={setPayInInstallments}
              planAllInAvailable={planAllInAvailable}
              planTotalCents={planTotalCents}
            />
          </StepSection>
        </div>

        <OrderSummary
          lines={lines}
          platformFeeCents={platformFeeCents}
          bookingFeeCents={bookingFeeCents}
          totalCents={totalCents}
          planActive={planActive}
          planLines={planLines}
          planTotalCents={planTotalCents}
          planInstallments={PAYMENT_PLAN_INSTALLMENTS}
          planVenueCents={planVenueCents}
          planBookingFeeCents={planBookingFeeCents}
          planPlatformFeeCents={planPlatformFeeCents}
        />
      </div>
    </section>
  );

  // Presale ticket-selling splash: logo + "Buy tickets" over the backdrop that
  // crossfades into the purchase flow. Progressive reveal, accommodation +
  // extras + checkout appear only once a ticket is in the order.
  const logo = story?.hero?.logo;
  // On-sale copy, its OWN heading/sub-line so on sale it doesn't leak the
  // presale text ("Sign up for the presale"). Dates are shared. Button falls
  // back to "Buy tickets". Editable in /admin/festival's "On-sale copy" block.
  const buyCopy = story?.splash?.buy ?? {};
  const buyDates = buyCopy.dates || story?.splash?.dates;
  const buyHeading = buyCopy.heading;
  const buySubline = buyCopy.subline;
  const buyCta = buyCopy.ctaLabel || "Buy tickets";
  // Subtitle under the Tickets step, organiser-editable (On-sale copy), with a
  // neutral fallback (no hardcoded "presale"/year).
  const ticketsSubtitle = buyCopy.ticketsSubtitle || "Secure your tickets";

  const buySplash = (
    <div className="relative min-h-[80vh]">
      {/* Purchase flow, fades in once the buyer taps Buy tickets. */}
      <div
        className={`transition-opacity duration-700 ${buyStarted ? "opacity-100" : "pointer-events-none opacity-0"}`}
      >
        <div className="lg:grid lg:grid-cols-[1fr_340px] lg:gap-8">
          <div className="space-y-8">
            {logo && (
              <img
                src={logo}
                alt={event.title}
                className="mx-auto w-52 max-w-[60vw] object-contain drop-shadow-[0_6px_24px_rgba(0,0,0,0.5)]"
              />
            )}
            <StepSection step={1} title="Tickets" subtitle={ticketsSubtitle}>
              {tiers.length === 0 ? (
                <EmptyNote>Tickets aren’t on sale yet. Check back soon.</EmptyNote>
              ) : (
                <div className="space-y-3">
                  {tiers.map((t) => (
                    <TierCard key={t.id} tier={t} qty={qty[t.id] ?? 0} onChange={(n) => setQuantity(t.id, n)} />
                  ))}
                </div>
              )}
            </StepSection>

            {hasTickets && accommodation.length > 0 && (
              <div className="festival-reveal">
                <StepSection step={2} title="Accommodation" subtitle="Somewhere to stay">
              <AccommodationSection accommodation={accommodation} qty={qty} setQuantity={setQuantity} bedLayouts={bedLayouts} setBedLayout={setBedLayout} />
                </StepSection>
              </div>
            )}

            {hasTickets && addOns.length > 0 && (
              <div className="festival-reveal">
                <StepSection step={3} title="Extras" subtitle="Add-ons for your trip">
                  <div className="space-y-3">
                    {addOns.map((a) => (
                      <AddOnCard key={a.id} addOn={a} qtyOf={(id) => qty[id] ?? 0} setQty={setQuantity} />
                    ))}
                  </div>
                </StepSection>
              </div>
            )}

            {hasTickets && workshopChoiceNeeded(tiers, workshopSlots, qty) && (
              <div className="festival-reveal">
                <StepSection step={4} title="Workshops" subtitle="Choose your session">
                  <WorkshopPicker
                    tiers={tiers}
                    sessions={sessions}
                    qty={qty}
                    picks={workshopPicks}
                    onChangeTier={setTierPick}
                  />
                </StepSection>
              </div>
            )}

            {hasTickets && (
              <div className="festival-reveal">
                <StepSection step={workshopChoiceNeeded(tiers, workshopSlots, qty) ? 5 : 4} title="Your details" subtitle="Almost there">
                  <CheckoutBlock
                    eventId={eventId}
                    currency={currency}
                    tiers={tiers}
                    addOns={[...addOns, ...accommodation]}
                    qty={qty}
                    bedLayouts={bedLayouts}
                    workshops={workshopSelections}
                    workshopsComplete={workshopsComplete}
                    totalCents={totalCents}
                    hasTickets={hasTickets}
                    paymentPlanFeeCents={event.paymentPlanFeeCents}
                    payInInstallments={payInInstallments}
                    setPayInInstallments={setPayInInstallments}
                    planAllInAvailable={planAllInAvailable}
                    planTotalCents={planTotalCents}
                  />
                </StepSection>
              </div>
            )}
          </div>

          <OrderSummary
          lines={lines}
          platformFeeCents={platformFeeCents}
          bookingFeeCents={bookingFeeCents}
          totalCents={totalCents}
          planActive={planActive}
          planLines={planLines}
          planTotalCents={planTotalCents}
          planInstallments={PAYMENT_PLAN_INSTALLMENTS}
          planVenueCents={planVenueCents}
          planBookingFeeCents={planBookingFeeCents}
          planPlatformFeeCents={planPlatformFeeCents}
        />
        </div>
      </div>

      {/* Intro overlay, fades out on Buy tickets. */}
      <div
        className={`absolute inset-0 flex flex-col items-center justify-center gap-9 px-4 text-center transition-opacity duration-700 ${buyStarted ? "pointer-events-none opacity-0" : "opacity-100"}`}
      >
        {logo ? (
          <img
            src={logo}
            alt={event.title}
            className="festival-logo-pulse w-72 max-w-[78vw] object-contain drop-shadow-[0_8px_30px_rgba(0,0,0,0.6)] md:w-[26rem]"
          />
        ) : (
          <h1 className="text-5xl font-black tracking-tight text-white drop-shadow-lg md:text-7xl">{event.title}</h1>
        )}
        {(buyDates || buyHeading || buySubline) && (
          <div className="max-w-md">
            {buyDates && (
              <p className="text-2xl font-black leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)] md:text-3xl">
                {buyDates}
              </p>
            )}
            {buyHeading && (
              <h2 className="text-2xl font-black leading-tight tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)] md:text-3xl">
                {buyHeading}
              </h2>
            )}
            {buySubline && (
              <p className="mt-2 text-sm text-white/80 drop-shadow-[0_1px_8px_rgba(0,0,0,0.7)] md:text-base">
                {buySubline}
              </p>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => setBuyStarted(true)}
          className="transition hover:brightness-105 active:scale-[0.98]"
          style={{
            height: "3.25rem",
            borderRadius: "13px",
            border: "none",
            padding: "0 2.5rem",
            fontSize: "1.05rem",
            fontWeight: 700,
            letterSpacing: "0.02em",
            backgroundColor: "var(--accent)",
            color: "var(--accent-fg)",
            boxShadow: "0 14px 40px -12px var(--accent)",
          }}
        >
          {buyCta}
        </button>
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (variant === "splash") {
    body =
      story?.splash?.mode === "buy" ? (
        buySplash
      ) : (
        <FestivalSplash
          logo={story?.hero?.logo}
          name={event.title}
          venueId={venueData?.venue?.id}
          ctaLabel={story?.splash?.ctaLabel ?? "Sign up"}
          dates={story?.splash?.dates}
          heading={story?.splash?.heading}
          subline={story?.splash?.subline}
          listId={story?.splash?.listId}
        />
      );
  } else if (variant === "lineup") {
    body = story ? <div className="pt-4">{<FestivalStoryTop content={story} only="lineup" />}</div> : null;
  } else if (variant === "whatsOn") {
    body = story ? <div className="pt-4">{<FestivalStoryTop content={story} only="expect" />}</div> : null;
  } else if (variant === "info") {
    body = story ? <div className="pt-4"><FestivalStoryBottom content={story} /></div> : null;
  } else if (variant === "tickets") {
    body = (
      <div className="space-y-6 pt-4">
        {purchasedNotice}
        {ticketsSection}
      </div>
    );
  } else {
    body = (
      <>
        {heroSection}
        {marketingSection}
        <div className="mt-12">{ticketsSection}</div>
        {story && <div className="mt-12"><FestivalStoryBottom content={story} /></div>}
      </>
    );
  }

  return (
    <div className="relative pb-24 lg:pb-8" style={themeStyle}>
      <FestivalFontFace url={story?.theme?.fontUrl} />
      {story && (
        <FestivalBackdrop
          // On-sale mode can carry its own backdrops; falls back to the defaults.
          image={(story.splash?.mode === "buy" && story.splash.buy?.backdrop) || story.backdrop}
          imagePortrait={
            (story.splash?.mode === "buy" && story.splash.buy?.backdropPortrait) || story.backdropPortrait
          }
          accent={story.theme?.accent}
        />
      )}
      {body}
    </div>
  );
}

// ── Order summary: sticky at lg+, fixed bottom bar on mobile ─────────────────
function OrderSummary({
  lines,
  platformFeeCents,
  bookingFeeCents,
  totalCents,
  planActive = false,
  planLines,
  planTotalCents,
  planInstallments = PAYMENT_PLAN_INSTALLMENTS,
  planVenueCents = 0,
  planBookingFeeCents = 0,
  planPlatformFeeCents = 0,
}: {
  lines: Array<{ id: string; name: string; qty: number; priceCents: number }>;
  platformFeeCents: number;
  bookingFeeCents: number;
  totalCents: number;
  planActive?: boolean;
  planLines?: Array<{ id: string; name: string; qty: number; priceCents: number }>;
  planTotalCents?: number;
  planInstallments?: number;
  planVenueCents?: number;
  planBookingFeeCents?: number;
  planPlatformFeeCents?: number;
}) {
  // On the all-in plan the price is one fixed number (e.g. £240) the venue set,
  // inclusive of everything. So the basket shows the item at that plan price and
  // presents the breakdown as what it actually is: the total split into equal
  // monthly payments (e.g. 3 × £80), not an itemised ticket-plus-fees sum.
  const showPlan = planActive && planTotalCents != null;
  const displayLines = showPlan && planLines ? planLines : lines;
  const displayTotal = showPlan && planTotalCents != null ? planTotalCents : totalCents;
  const perMonthCents = showPlan && planTotalCents != null ? Math.ceil(planTotalCents / planInstallments) : 0;
  // Dated schedule the buyer will actually be charged: first payment today,
  // then the same amount each month. Mirrors the Stripe subscription the
  // checkout creates (fixed monthly price × installments, deposit taken now).
  const planSchedule = showPlan
    ? Array.from({ length: planInstallments }, (_, i) => {
        const d = new Date();
        const day = d.getDate();
        d.setDate(1);
        d.setMonth(d.getMonth() + i);
        const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        d.setDate(Math.min(day, daysInMonth));
        return {
          when: i === 0 ? "First payment today" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }),
          amountCents: perMonthCents,
        };
      })
    : [];
  const itemCount = displayLines.reduce((n, l) => n + l.qty, 0);
  const [mobileOpen, setMobileOpen] = useState(false);

  // The order breakdown, shared verbatim by the desktop sidebar and the
  // mobile expandable sheet so the two can never drift.
  const breakdown = (
    <>
      <h2 className="text-lg font-bold text-[var(--foreground)]">Your order</h2>
      {displayLines.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--muted)]">Nothing selected yet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {displayLines.map((l) => (
            <li key={l.id} className="flex items-start justify-between gap-3 text-sm">
              <span className="text-[var(--foreground)]">
                {l.qty} × {l.name}
              </span>
              <span className="shrink-0 tabular-nums text-[var(--foreground)]/80">
                {gbp(l.qty * l.priceCents)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!showPlan && platformFeeCents > 0 && lines.length > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-[var(--foreground)]/70">Platform fee</span>
          <span className="shrink-0 tabular-nums text-[var(--foreground)]/80">
            {gbp(platformFeeCents)}
          </span>
        </div>
      )}
      {!showPlan && bookingFeeCents > 0 && lines.length > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-[var(--foreground)]/70">Booking fee</span>
          <span className="shrink-0 tabular-nums text-[var(--foreground)]/80">
            {gbp(bookingFeeCents)}
          </span>
        </div>
      )}
      <div className="mt-4 flex items-center justify-between border-t border-[var(--border-default)] pt-4">
        <span className="text-sm font-medium text-[var(--muted)]">Total</span>
        <span className="text-xl font-black tabular-nums text-[var(--foreground)]">
          {gbp(displayTotal)}
        </span>
      </div>
      {showPlan && (
        <>
          <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
            Payment plan · {planInstallments} × {gbp(perMonthCents)}/mo
          </p>
          <div className="mt-2 space-y-2">
            {planSchedule.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-[var(--foreground)]/70">{p.when}</span>
                <span className="shrink-0 tabular-nums text-[var(--foreground)]/80">{gbp(p.amountCents)}</span>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
            Where your {gbp(displayTotal)} goes
          </p>
          <div className="mt-2 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--foreground)]/70">Tickets</span>
              <span className="shrink-0 tabular-nums text-[var(--foreground)]/80">{gbp(planVenueCents)}</span>
            </div>
            {planBookingFeeCents > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-[var(--foreground)]/70">Booking fee</span>
                <span className="shrink-0 tabular-nums text-[var(--foreground)]/80">{gbp(planBookingFeeCents)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--foreground)]/70">Platform fee</span>
              <span className="shrink-0 tabular-nums text-[var(--foreground)]/80">{gbp(planPlatformFeeCents)}</span>
            </div>
          </div>
          <p className="mt-4 text-xs leading-snug text-[var(--muted)]">
            No interest and no credit check, the {gbp(displayTotal)} total is simply split
            across {planInstallments} months. The first payment is taken today; the rest are
            collected automatically each month.
          </p>
        </>
      )}
    </>
  );

  return (
    <>
      {/* Desktop: sticky sidebar */}
      <aside className="hidden lg:block">
        <div className="sticky top-20 rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-5">
          {breakdown}
        </div>
      </aside>

      {/* Mobile: running-total bar that expands into the full breakdown. */}
      {displayLines.length > 0 && (
        <div className="fixed inset-x-0 bottom-16 z-30 lg:hidden">
          {mobileOpen && (
            <div className="max-h-[55vh] overflow-y-auto border-t border-[var(--border-default)] bg-[var(--surface)] px-4 pb-4 pt-3 shadow-[0_-10px_28px_-14px_rgba(0,0,0,0.45)]">
              {breakdown}
            </div>
          )}
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            aria-expanded={mobileOpen}
            className="flex w-full items-center justify-between border-0 border-t border-[var(--border-default)] bg-[var(--surface)]/95 px-4 py-3 text-left backdrop-blur"
          >
            <span className="flex items-center gap-1.5 text-sm text-[var(--muted)]">
              {itemCount} item{itemCount === 1 ? "" : "s"}
              {showPlan ? ` · ${planInstallments} × ${gbp(perMonthCents)}/mo` : ""}
              <span className="text-[var(--accent)]">· {mobileOpen ? "Hide" : "Breakdown"}</span>
              <span
                aria-hidden
                className="text-[10px] transition-transform duration-200"
                style={{ transform: mobileOpen ? "rotate(180deg)" : "none" }}
              >
                ▲
              </span>
            </span>
            <span className="text-lg font-black tabular-nums text-[var(--foreground)]">
              {gbp(displayTotal)}
            </span>
          </button>
        </div>
      )}
    </>
  );
}

// ── Section wrapper with a step number ──────────────────────────────────────
function StepSection({
  step,
  title,
  subtitle,
  children,
}: {
  step: number;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-4 flex items-center gap-3">
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold"
          style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
        >
          {step}
        </span>
        <div>
          <h2 className="text-xl font-bold leading-tight text-[var(--foreground)]">{title}</h2>
          {subtitle && <p className="text-sm text-[var(--muted)]">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-5 text-center text-sm text-[var(--muted)]">
      {children}
    </p>
  );
}

// ── Quantity stepper ────────────────────────────────────────────────────────
function Stepper({
  value,
  onChange,
  max,
  disabled,
}: {
  value: number;
  onChange: (n: number) => void;
  max: number | null; // null = uncapped
  disabled?: boolean;
}) {
  const atMax = max != null && value >= max;
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        aria-label="Decrease"
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={disabled || value <= 0}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border-default)] text-lg text-[var(--foreground)] transition duration-150 hover:border-[var(--accent)] active:scale-90 disabled:opacity-40 disabled:hover:border-[var(--border-default)]"
      >
        −
      </button>
      <span className="w-6 text-center tabular-nums text-[var(--foreground)]">{value}</span>
      <button
        type="button"
        aria-label="Increase"
        onClick={() => onChange(value + 1)}
        disabled={disabled || atMax}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border-default)] text-lg text-[var(--foreground)] transition duration-150 hover:border-[var(--accent)] active:scale-90 disabled:opacity-40 disabled:hover:border-[var(--border-default)]"
      >
        +
      </button>
    </div>
  );
}

// ── Ticket tier card ────────────────────────────────────────────────────────
function TierCard({
  tier,
  qty,
  onChange,
}: {
  tier: FestivalTier;
  qty: number;
  onChange: (n: number) => void;
}) {
  // Live clock: an upcoming tier (future salesStart) ticks every second so the
  // countdown updates AND the row auto-flips to buyable the instant it opens,
  // no page refresh needed. Stops ticking once open / sold out / closed.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const isUpcoming = tier.salesStart != null && new Date(tier.salesStart).getTime() > nowMs;
  useEffect(() => {
    if (!isUpcoming) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isUpcoming]);
  const state = tierSaleState(tier, new Date(nowMs));
  return (
    <div
      className={`rounded-[var(--radius)] border bg-[var(--surface)] p-4 transition-[border-color,box-shadow] duration-300 ${
        qty > 0
          ? "border-[var(--accent)] shadow-[0_10px_36px_-18px_var(--accent)]"
          : "border-[var(--border-default)]"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-semibold text-[var(--foreground)]">{tier.name}</p>
          <p className="mt-0.5 text-sm text-[var(--foreground)]/70">{gbp(tier.priceCents)}</p>
          {tier.description && (
            <p className="mt-1 whitespace-pre-line text-xs text-[var(--muted)]">
              {tier.description}
            </p>
          )}
          {/* Scarcity copy removed 2026-09-05 — venues don't want buyer-facing
              remaining counts (leaks capacity + feels manipulative). Sold-out
              state is still surfaced elsewhere. */}
          {state.kind === "upcoming" && (
            <>
              <p className="mt-1 text-xs text-[var(--muted)]">{onSaleLabel(state.startsAt)}</p>
              <p className="mt-0.5 text-xs font-semibold text-[var(--accent)]">
                Opens in {formatCountdown(state.startsAt.getTime() - nowMs)}
              </p>
            </>
          )}
          {tier.isPresale && (
            <span className="mt-1 inline-block rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[11px] uppercase tracking-wide text-[var(--muted)]">
              Presale
            </span>
          )}
        </div>
        {state.kind === "soldout" ? (
          <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-3 py-1 text-sm text-[var(--muted)]">
            Sold out
          </span>
        ) : state.kind === "upcoming" ? (
          <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-3 py-1 text-sm text-[var(--muted)]">
            Not yet on sale
          </span>
        ) : state.kind === "ended" ? (
          <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-3 py-1 text-sm text-[var(--muted)]">
            Sales closed
          </span>
        ) : (
          <Stepper value={qty} onChange={onChange} max={state.cap} />
        )}
      </div>
    </div>
  );
}

// ── Extra (add-on) card ─────────────────────────────────────────────────────
// Sized extras (T-shirts with variants) show a stepper PER size, each capped by
// that size's remaining stock. Unsized extras keep the single stepper.
function AddOnCard({
  addOn,
  qtyOf,
  setQty,
}: {
  addOn: FestivalAddOn;
  qtyOf: (id: string) => number;
  setQty: (id: string, n: number) => void;
}) {
  const sized = addOn.variants && addOn.variants.length > 0;
  const selectedTotal = sized
    ? addOn.variants.reduce((s, v) => s + qtyOf(v.id), 0)
    : qtyOf(addOn.id);
  const soldOut = sized
    ? addOn.variants.every((v) => v.soldOut)
    : addOn.remaining != null && addOn.remaining <= 0;
  const hasImages = (addOn.imageUrls?.length ?? 0) > 0;
  return (
    <div
      className={`overflow-hidden rounded-[var(--radius)] border bg-[var(--surface)] transition-[border-color,box-shadow] duration-300 ${
        selectedTotal > 0
          ? "border-[var(--accent)] shadow-[0_10px_36px_-18px_var(--accent)]"
          : "border-[var(--border-default)]"
      }`}
    >
      {/* Photos (T-shirts, parking, any extra), same gallery as accommodation. */}
      {hasImages && <ImageGallery images={addOn.imageUrls} alt={addOn.name} />}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-semibold text-[var(--foreground)]">{addOn.name}</p>
            {addOn.description && (
              <p className="mt-0.5 text-sm text-[var(--foreground)]/70">{addOn.description}</p>
            )}
            <p className="mt-1 text-sm text-[var(--foreground)]/70">{gbp(addOn.priceCents)}</p>
          </div>
          {!sized &&
            (soldOut ? (
              <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-3 py-1 text-sm text-[var(--muted)]">
                Sold out
              </span>
            ) : (
              <Stepper value={qtyOf(addOn.id)} onChange={(n) => setQty(addOn.id, n)} max={capOf(addOn.remaining, null)} />
            ))}
        </div>

        {/* Per-size selectors */}
        {sized && (
          <div className="mt-3 flex flex-col gap-2 border-t border-[var(--border-default)] pt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">Size</p>
            {addOn.variants.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--foreground)]">{v.label}</span>
                {v.soldOut ? (
                  <span className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-xs text-[var(--muted)]">Sold out</span>
                ) : (
                  <Stepper value={qtyOf(v.id)} onChange={(n) => setQty(v.id, n)} max={capOf(v.remaining, null)} />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Accommodation, grouped ───────────────────────────────────────────────────
// Madruga-style listings arrive as one row per size ("Glamping [2 Persons]",
// "Glamping [3 Persons]", …). A flat wall of near-identical cards is hard to
// scan, so rows sharing a base name collapse into ONE card: photos + "from £"
// on the group, then a smooth expand to pick the size. Groups of one render
// the classic card unchanged. Pure presentation, the group key is derived
// from the name's " [ … ]" suffix, no data changes.
function accommodationGroups(listings: FestivalAddOn[]): Array<{ base: string; items: FestivalAddOn[] }> {
  const order: string[] = [];
  const byBase = new Map<string, FestivalAddOn[]>();
  for (const l of listings) {
    const base = l.name.replace(/\s*\[[^\]]*\]\s*$/, "").trim() || l.name;
    if (!byBase.has(base)) {
      byBase.set(base, []);
      order.push(base);
    }
    byBase.get(base)!.push(l);
  }
  return order.map((base) => ({ base, items: byBase.get(base)! }));
}

function variantLabel(l: FestivalAddOn): string {
  const m = l.name.match(/\[([^\]]*)\]\s*$/);
  if (m) return m[1];
  return l.sleeps ? `Sleeps ${l.sleeps}` : l.name;
}

// Bed-setup toggle pill. Explicit backgrounds for BOTH states so nothing falls
// through to the global `button { background: var(--accent) }` skin: selected =
// solid accent with near-black text (high contrast on the peach/orange accent);
// unselected = transparent outline with readable foreground text.
function BedLayoutPill({
  label,
  on,
  onClick,
  size = "sm",
}: {
  label: string;
  on: boolean;
  onClick: () => void;
  size?: "xs" | "sm";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`rounded-full border font-semibold transition-all duration-200 ${
        size === "xs" ? "px-3 py-1.5 text-xs" : "px-3.5 py-1.5 text-sm"
      } ${
        on
          ? "border-[var(--accent)]"
          : "border-[var(--border-default)] text-[var(--foreground)] hover:border-[var(--border-strong)]"
      }`}
      style={on ? { backgroundColor: "var(--accent)", color: "#1b1b1b" } : { backgroundColor: "transparent" }}
    >
      {label}
    </button>
  );
}

function AccommodationSection({
  accommodation,
  qty,
  setQuantity,
  bedLayouts,
  setBedLayout,
}: {
  accommodation: FestivalAddOn[];
  qty: Record<string, number>;
  setQuantity: (id: string, n: number) => void;
  bedLayouts: Record<string, string>;
  setBedLayout: (id: string, label: string) => void;
}) {
  return (
    <div className="space-y-5">
      {accommodationGroups(accommodation).map((g) =>
        g.items.length === 1 ? (
          <AccommodationCard
            key={g.items[0].id}
            listing={g.items[0]}
            qty={qty[g.items[0].id] ?? 0}
            onChange={(n) => setQuantity(g.items[0].id, n)}
            bedLayout={bedLayouts[g.items[0].id]}
            onBedLayout={(l) => setBedLayout(g.items[0].id, l)}
          />
        ) : (
          <AccommodationGroupCard
            key={g.base}
            base={g.base}
            items={g.items}
            qty={qty}
            setQuantity={setQuantity}
            bedLayouts={bedLayouts}
            setBedLayout={setBedLayout}
          />
        ),
      )}
    </div>
  );
}

function AccommodationGroupCard({
  base,
  items,
  qty,
  setQuantity,
  bedLayouts,
  setBedLayout,
}: {
  base: string;
  items: FestivalAddOn[];
  qty: Record<string, number>;
  setQuantity: (id: string, n: number) => void;
  bedLayouts: Record<string, string>;
  setBedLayout: (id: string, label: string) => void;
}) {
  const selectedTotal = items.reduce((s, l) => s + (qty[l.id] ?? 0), 0);
  const [open, setOpen] = useState(false);
  const expanded = open || selectedTotal > 0; // never collapse a card that's in the order
  const images = items.find((l) => (l.imageUrls?.length ?? 0) > 0)?.imageUrls ?? [];
  const prices = items.map((l) => l.priceCents);
  const fromPrice = Math.min(...prices);
  const sleepsRange = (() => {
    const ss = items.map((l) => l.sleeps).filter((n): n is number => n != null);
    if (!ss.length) return null;
    const lo = Math.min(...ss);
    const hi = Math.max(...ss);
    return lo === hi ? `Sleeps ${lo}` : `Sleeps ${lo} to ${hi}`;
  })();
  const allSoldOut = items.every((l) => l.remaining != null && l.remaining <= 0);
  const description = items.find((l) => l.description)?.description;

  return (
    <div
      className={`overflow-hidden rounded-[var(--radius)] border bg-[var(--surface)] transition-[border-color,box-shadow] duration-300 ${
        selectedTotal > 0
          ? "border-[var(--accent)] shadow-[0_10px_36px_-18px_var(--accent)]"
          : "border-[var(--border-default)]"
      }`}
    >
      {images.length > 0 && <ImageGallery images={images} alt={base} />}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={expanded}
        className="w-full border-0 bg-transparent p-4 text-left"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-semibold text-[var(--foreground)]">{base}</p>
            {sleepsRange && (
              <p className="mt-0.5 text-xs font-medium uppercase tracking-wide text-[var(--accent)]">{sleepsRange}</p>
            )}
            {description && <p className="mt-0.5 text-sm text-[var(--foreground)]/70">{description}</p>}
            <p className="mt-1 text-sm text-[var(--foreground)]/70">from {gbp(fromPrice)}</p>
          </div>
          <span className="flex shrink-0 items-center gap-2">
            {allSoldOut ? (
              <span className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-sm text-[var(--muted)]">Sold out</span>
            ) : selectedTotal > 0 ? (
              <span className="rounded-full px-3 py-1 text-sm font-semibold" style={{ backgroundColor: "color-mix(in srgb, var(--accent) 16%, transparent)", color: "var(--foreground)" }}>
                {selectedTotal} selected
              </span>
            ) : (
              <span className="rounded-full border border-[var(--border-default)] px-3.5 py-1.5 text-sm text-[var(--subtle)]">
                Choose size
              </span>
            )}
            <span
              aria-hidden
              className="text-xs text-[var(--muted)] transition-transform duration-300"
              style={{ transform: expanded ? "rotate(180deg)" : "none" }}
            >
              ▼
            </span>
          </span>
        </div>
      </button>

      {/* Smooth expand: grid-rows trick animates height without measuring. */}
      <div
        className="grid transition-[grid-template-rows] duration-300 ease-out"
        style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="flex flex-col gap-2 border-t border-[var(--border-default)] p-4">
            {items.map((l) => {
              const soldOut = l.remaining != null && l.remaining <= 0;
              const selected = (qty[l.id] ?? 0) > 0;
              const layouts = l.sleeps && l.bedChoice ? bedLayoutsForSleeps(l.sleeps) : [];
              const effectiveLayout = bedLayouts[l.id] ?? layouts[0]?.label;
              return (
                <div key={l.id} className="rounded-[var(--radius-sm)] px-1 py-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="min-w-0 text-sm text-[var(--foreground)]">
                      <span className="font-medium">{variantLabel(l)}</span>
                      <span className="ml-2 text-[var(--foreground)]/60">{gbp(l.priceCents)}</span>
                    </span>
                    {soldOut ? (
                      <span className="rounded-full bg-[var(--surface-2)] px-3 py-1 text-xs text-[var(--muted)]">Sold out</span>
                    ) : (
                      <Stepper value={qty[l.id] ?? 0} onChange={(n) => setQuantity(l.id, n)} max={capOf(l.remaining, null)} />
                    )}
                  </div>
                  {selected && layouts.length > 1 && (
                    <div className="festival-reveal mt-2">
                      <p className="mb-1.5 text-xs font-medium text-[var(--subtle)]">Bed setup</p>
                      <div className="flex flex-wrap gap-2">
                        {layouts.map((bl) => (
                          <BedLayoutPill
                            key={bl.label}
                            label={bl.label}
                            on={bl.label === effectiveLayout}
                            onClick={() => setBedLayout(l.id, bl.label)}
                            size="xs"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Accommodation card with a horizontally scrollable image gallery ──────────
function AccommodationCard({
  listing,
  qty,
  onChange,
  bedLayout,
  onBedLayout,
}: {
  listing: FestivalAddOn;
  qty: number;
  onChange: (n: number) => void;
  bedLayout?: string;
  onBedLayout?: (label: string) => void;
}) {
  const soldOut = listing.remaining != null && listing.remaining <= 0;
  const cap = capOf(listing.remaining, null);
  const selected = qty > 0;
  const layouts = listing.sleeps && listing.bedChoice ? bedLayoutsForSleeps(listing.sleeps) : [];
  const effectiveLayout = bedLayout ?? layouts[0]?.label;
  return (
    <div
      className={`overflow-hidden rounded-[var(--radius)] border bg-[var(--surface)] transition-[border-color,box-shadow,transform] duration-300 ${
        selected
          ? "border-[var(--accent)] shadow-[0_10px_36px_-18px_var(--accent)]"
          : "border-[var(--border-default)]"
      }`}
    >
      <ImageGallery images={listing.imageUrls} alt={listing.name} />
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-semibold text-[var(--foreground)]">{listing.name}</p>
            {listing.sleeps ? (
              <p className="mt-0.5 text-xs font-medium uppercase tracking-wide text-[var(--accent)]">
                Sleeps {listing.sleeps}
              </p>
            ) : null}
            {listing.description && (
              <p className="mt-0.5 text-sm text-[var(--foreground)]/70">{listing.description}</p>
            )}
            <p className="mt-1 text-sm text-[var(--foreground)]/70">{gbp(listing.priceCents)}</p>
          </div>
          {soldOut ? (
            <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-3 py-1 text-sm text-[var(--muted)]">
              Sold out
            </span>
          ) : (
            <Stepper value={qty} onChange={onChange} max={cap} />
          )}
        </div>

        {/* Bed setup, revealed once this unit is in the order. */}
        {selected && layouts.length > 1 && (
          <div className="festival-reveal mt-4 border-t border-[var(--border-default)] pt-3">
            <p className="mb-2 text-xs font-medium text-[var(--subtle)]">Bed setup</p>
            <div className="flex flex-wrap gap-2">
              {layouts.map((l) => (
                <BedLayoutPill
                  key={l.label}
                  label={l.label}
                  on={l.label === effectiveLayout}
                  onClick={() => onBedLayout?.(l.label)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Gallery. Up to 2 photos render as a clean side-by-side grid (no arrows/dots,
// everything's already visible). 3+ photos use a swipeable/scrollable carousel
// with arrow buttons (desktop) + dot indicators tracking the active photo.
function ImageGallery({ images, alt }: { images: string[]; alt: string }) {
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  if (!images || images.length === 0) return null;

  // 1–2 photos: no carousel chrome, just show them.
  if (images.length <= 2) {
    return (
      <div className={`grid gap-2 p-2 ${images.length === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
        {images.map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={src}
            alt={`${alt}, photo ${i + 1}`}
            loading="lazy"
            className="aspect-[3/2] w-full rounded-[var(--radius-sm)] object-cover"
          />
        ))}
      </div>
    );
  }

  const scrollToItem = (i: number) => {
    const el = scroller.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(images.length - 1, i));
    const child = el.children[clamped] as HTMLElement | undefined;
    if (child) el.scrollTo({ left: child.offsetLeft - el.offsetLeft, behavior: "smooth" });
  };
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    // Active = the item whose left edge is nearest the scroll position.
    let nearest = 0, best = Infinity;
    for (let i = 0; i < el.children.length; i++) {
      const c = el.children[i] as HTMLElement;
      const d = Math.abs(c.offsetLeft - el.offsetLeft - el.scrollLeft);
      if (d < best) { best = d; nearest = i; }
    }
    setActive(nearest);
  };

  const arrow = (dir: -1 | 1, label: string, d: string) => (
    <button
      type="button"
      aria-label={label}
      onClick={() => scrollToItem(active + dir)}
      className="absolute top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full bg-black/50 p-1.5 text-white backdrop-blur transition hover:bg-black/70 sm:flex"
      style={{ [dir < 0 ? "left" : "right"]: "0.5rem" } as React.CSSProperties}
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2}>
        <path d={d} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );

  return (
    <div className="relative">
      <div
        ref={scroller}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth p-2"
      >
        {images.map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={src}
            alt={`${alt}, photo ${i + 1}`}
            loading="lazy"
            className="aspect-[3/2] w-[85%] shrink-0 snap-center rounded-[var(--radius-sm)] object-cover sm:w-[46%]"
          />
        ))}
      </div>
      {images.length > 1 && (
        <>
          {active > 0 && arrow(-1, "Previous photo", "M15 19l-7-7 7-7")}
          {active < images.length - 1 && arrow(1, "Next photo", "M9 5l7 7-7 7")}
          <div className="mt-1 flex justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to photo ${i + 1}`}
                onClick={() => scrollToItem(i)}
                className={`h-1.5 rounded-full transition-all ${i === active ? "w-4 bg-[var(--accent)]" : "w-1.5 bg-[var(--muted)]/50"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Upper bound for a stepper given remaining stock + a per-order cap. null = no cap.
function capOf(remaining: number | null, maxPerOrder: number | null): number | null {
  const caps = [remaining, maxPerOrder].filter((v): v is number => v != null);
  if (caps.length === 0) return null;
  return Math.max(0, Math.min(...caps));
}

// ── Checkout block: details (guest/member) + consent + payment plan ──────────
function CheckoutBlock({
  eventId,
  currency,
  tiers,
  addOns,
  qty,
  bedLayouts,
  workshops,
  workshopsComplete,
  totalCents,
  hasTickets,
  paymentPlanFeeCents,
  payInInstallments,
  setPayInInstallments,
  planAllInAvailable,
  planTotalCents,
}: {
  eventId: string;
  currency: string;
  tiers: FestivalTier[];
  addOns: FestivalAddOn[];
  qty: Quantities;
  bedLayouts: Record<string, string>;
  workshops: Array<{ tierId: string; sessionIds: string[] }>;
  workshopsComplete: boolean;
  totalCents: number;
  hasTickets: boolean;
  paymentPlanFeeCents: number;
  payInInstallments: boolean;
  setPayInInstallments: (v: boolean) => void;
  planAllInAvailable: boolean;
  planTotalCents: number;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const errRef = useRef<HTMLParagraphElement>(null);
  // Hard in-flight guard. `disabled` only updates on the next render, so a very
  // fast double-tap on a slow phone can run this handler twice before the button
  // disables, creating two pending orders + two Stripe sessions. The ref flips
  // synchronously, so the second call returns immediately.
  const submitting = useRef(false);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  // Guests re-key their email so a typo can't send tickets into the void (their
  // only route back to the ticket is that address). Compared case-insensitively.
  const emailsMatch = email.trim().toLowerCase() === confirmEmail.trim().toLowerCase();
  const guestReady = user ? true : name.trim() !== "" && emailValid && emailsMatch;
  // Need at least one ticket in the order, consent, (if guest) valid details,
  // and, when a workshop tier is in the cart, a complete workshop choice.
  const canCheckout = hasTickets && agreed && guestReady && workshopsComplete && !loading;
  // Payment plans are opt-in per tier (admin toggle). Offer the option only when
  // every selected ticket tier allows it, the backend enforces the same rule.
  const selectedTiers = tiers.filter((t) => (qty[t.id] ?? 0) > 0);
  const paymentPlansAvailable =
    selectedTiers.length > 0 && selectedTiers.every((t) => t.paymentPlanEnabled);
  // planAllInAvailable + planTotalCents come from the parent (so the basket
  // summary and this block agree). The server is authoritative; these drive the
  // buyer-facing "£X total, £Y/mo" display. perInstallment splits the plan total.
  const perInstallmentCents = Math.ceil(planTotalCents / PAYMENT_PLAN_INSTALLMENTS);

  async function go() {
    if (submitting.current) return;
    submitting.current = true;
    setLoading(true);
    // Client-side dead-man: if we're still "loading" after 10s, something
    // broke silently between "API returned URL" and "window.location.href
    // fired" (see analytics.ts commentary). Reset so the buyer can retry
    // rather than stare at "Starting checkout…" forever. On a successful
    // navigation the page unmounts before this timer fires.
    const deadman = window.setTimeout(() => {
      submitting.current = false;
      setLoading(false);
      pushToast("Taking longer than expected. Try again if nothing happens.");
    }, 10000);
    try {
      const body = {
        tiers: tiers
          .map((t) => ({ tierId: t.id, quantity: qty[t.id] ?? 0 }))
          .filter((l) => l.quantity > 0),
        addOns: addOns
          .flatMap((a): Array<{ addOnId: string; quantity: number; bedLayout?: string; variantId?: string }> => {
            // Sized add-on: one line per chosen size (keyed by variantId).
            if (a.variants && a.variants.length > 0) {
              return a.variants.map((v) => ({ addOnId: a.id, variantId: v.id, quantity: qty[v.id] ?? 0 }));
            }
            const quantity = qty[a.id] ?? 0;
            // Attach the chosen bed layout for accommodation (defaults to the
            // first option when the buyer didn't explicitly pick one). Only
            // units with bedChoice on (glamping) offer a choice, a fixed-setup
            // room sends no layout (mirrors the picker gate above).
            const layouts = a.sleeps && a.bedChoice ? bedLayoutsForSleeps(a.sleeps) : [];
            const bedLayout =
              layouts.length > 0 ? (bedLayouts[a.id] ?? layouts[0]?.label) : undefined;
            return [{ addOnId: a.id, quantity, ...(bedLayout ? { bedLayout } : {}) }];
          })
          .filter((l) => l.quantity > 0),
        termsAccepted: agreed,
        ...(user ? {} : { guest: { name: name.trim(), email: email.trim() } }),
        ...(payInInstallments && paymentPlansAvailable
          ? { paymentPlan: { installments: PAYMENT_PLAN_INSTALLMENTS } }
          : {}),
        ...(workshops.length > 0 ? { workshops } : {}),
      };
      const { checkoutUrl } = await createFestivalCheckout(eventId, body);
      pushEcommerceEventThenNavigate(
        "begin_checkout",
        {
          currency,
          value: totalCents / 100,
          items: [
            ...tiers
              .filter((t) => (qty[t.id] ?? 0) > 0)
              .map((t) => ({
                item_id: t.id,
                item_name: t.name,
                item_category: "ticket",
                price: t.priceCents / 100,
                quantity: qty[t.id],
              })),
            ...addOns
              .filter((a) => (qty[a.id] ?? 0) > 0)
              .map((a) => ({
                item_id: a.id,
                item_name: a.name,
                item_category: "add-on",
                price: a.priceCents / 100,
                quantity: qty[a.id],
              })),
          ],
        },
        checkoutUrl,
      );
    } catch (e) {
      // Only reset the guard on failure, on success we navigate to Stripe and
      // the page unmounts, so the button must stay locked until then.
      window.clearTimeout(deadman);
      submitting.current = false;
      pushToast(e instanceof Error ? e.message : "Couldn’t start checkout");
      setLoading(false);
      requestAnimationFrame(() =>
        errRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
      );
    }
  }

  return (
    <div className="rounded-[var(--radius)] border border-[var(--border-default)] bg-[var(--surface)] p-5">
      {!hasTickets && (
        <p className="mb-4 text-sm text-[var(--muted)]">
          Add at least one ticket above to continue.
        </p>
      )}

      {/* Guest details (signed-out only) */}
      {!user && (
        <div className="mb-4 space-y-3">
          <button
            type="button"
            onClick={() => navigate({ to: "/sign-in" })}
            className="w-full rounded-[var(--radius)] bg-[var(--accent)] px-4 py-3 text-sm font-bold"
            style={{ color: "var(--accent-fg)" }}
          >
            Sign in or create an account
          </button>
          <p className="text-center text-xs text-[var(--muted)]">
            Recommended, and keeps your tickets in one place.
          </p>
          <div className="flex items-center gap-3 text-xs text-[var(--muted)]">
            <span className="h-px flex-1 bg-[var(--border-default)]" />
            or continue as a guest
            <span className="h-px flex-1 bg-[var(--border-default)]" />
          </div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            autoComplete="name"
            className="w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-2)] p-3 text-[var(--foreground)]"
          />
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email for your tickets"
            className="w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-2)] p-3 text-[var(--foreground)]"
          />
          <input
            type="email"
            inputMode="email"
            autoComplete="off"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            onPaste={(e) => e.preventDefault()}
            placeholder="Confirm your email"
            className="w-full rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-2)] p-3 text-[var(--foreground)]"
          />
          {confirmEmail.trim() !== "" && !emailsMatch && (
            <p className="text-xs text-red-400">Emails don’t match. Check for a typo so your tickets reach you.</p>
          )}
        </div>
      )}

      {/* Payment plan toggle, shown only when every selected tier allows it. */}
      {paymentPlansAvailable && (
        <label className="mb-4 flex cursor-pointer items-start gap-3 rounded-[var(--radius-sm)] border border-[var(--border-default)] p-3">
          <input
            type="checkbox"
            checked={payInInstallments}
            onChange={(e) => setPayInInstallments(e.target.checked)}
            className="mt-0.5"
            style={{ accentColor: "var(--accent)" }}
          />
          <span className="text-sm text-[var(--foreground)]">
            Pay in {PAYMENT_PLAN_INSTALLMENTS} monthly installments
            <span className="mt-0.5 block text-xs text-[var(--muted)]">
              {planAllInAvailable
                ? `${gbp(planTotalCents)} total, ${gbp(perInstallmentCents)}/mo × ${PAYMENT_PLAN_INSTALLMENTS}.`
                : totalCents > 0
                  ? `Spread the cost, about ${gbp(perInstallmentCents)}/mo${paymentPlanFeeCents > 0 ? ` (includes a ${gbp(paymentPlanFeeCents)} plan fee)` : ""}.`
                  : "Collected monthly."}
            </span>
          </span>
        </label>
      )}

      {/* Consent */}
      <label className="flex items-start gap-2 text-xs text-[var(--muted)]">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5"
          style={{ accentColor: "var(--accent)" }}
        />
        <span>
          I agree to the{" "}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline">
            terms &amp; privacy policy
          </a>
          , and that my details are used to manage this order and shared with the organiser.
        </span>
      </label>

      <button
        type="button"
        onClick={go}
        disabled={!canCheckout}
        className="mt-4 w-full rounded-[var(--radius)] bg-[var(--accent)] px-4 py-3 text-sm font-bold disabled:opacity-50"
        style={{ color: "var(--accent-fg)" }}
      >
        {loading
          ? "Starting checkout…"
          : payInInstallments && paymentPlansAvailable
            ? `Checkout · ${gbp(planTotalCents)} (${gbp(perInstallmentCents)}/mo)`
            : totalCents > 0
              ? `Checkout · ${gbp(totalCents)}`
              : "Checkout"}
      </button>
      <p ref={errRef} className="sr-only" aria-live="polite" />
    </div>
  );
}
